import React from 'react';
import { FONT_OPTIONS } from './data';

// DesignerTopContextBar — context-sensitive toolbar that sits under the
// top bar. Visible only when an element is selected. Contents are
// type-aware: text gets font/size/colour/B-I-U/align; shape gets
// fill/stroke; image gets replace/delete; amount gets font/size/colour.

// ─── Icons ───
const IconBold = () => <strong className="v2-cd-ctx-letter">B</strong>;
const IconItalic = () => <em className="v2-cd-ctx-letter v2-cd-ctx-letter--italic">I</em>;
const IconUnderline = () => <span className="v2-cd-ctx-letter v2-cd-ctx-letter--underline">U</span>;
const IconAlignLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
    <path d="M2 4h12M2 8h8M2 12h12" />
  </svg>
);
const IconAlignCenter = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
    <path d="M2 4h12M4 8h8M2 12h12" />
  </svg>
);
const IconAlignRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
    <path d="M2 4h12M6 8h8M2 12h12" />
  </svg>
);
const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 4l3 3 3-3" />
  </svg>
);
// Corner-radius glyph — an L-bend with a rounded elbow, reads as "rounded
// corner". Sits beside the Roundness slider for shapes.
const IconCorner = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 14V8a5 5 0 0 1 5-5h6" />
  </svg>
);

// Roundness — slider that drives the shape's corner radius (0–100).
const RoundnessControl = ({ value, onChange }) => (
  <div className="v2-cd-ctx-radius" title="Corner roundness">
    <IconCorner />
    <input
      type="range"
      min="0"
      max="100"
      step="1"
      value={value || 0}
      onChange={(e) => onChange({ radius: parseInt(e.target.value, 10) })}
      className="v2-cd-ctx-range"
      aria-label="Corner roundness"
    />
  </div>
);

// Outline glyph — a hollow rounded square, reads as "border".
const IconOutline = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
    <rect x="2.5" y="2.5" width="11" height="11" rx="3" />
  </svg>
);

// Outline — a width stepper (0 = no border) plus a colour swatch that opens
// the stroke colour panel once there's a border to colour.
const OutlineControl = ({ width, color, onChangeWidth, onOpenColor }) => {
  const w = width || 0;
  const clamp = (n) => Math.max(0, Math.min(12, n));
  return (
    <div className="v2-cd-ctx-outline" title="Outline">
      <IconOutline />
      <div className="v2-cd-ctx-stepper">
        <button type="button" className="v2-cd-ctx-stepper-btn" onClick={() => onChangeWidth(clamp(w - 1))} aria-label="Thinner outline">−</button>
        <span className="v2-cd-ctx-stepper-value">{w}</span>
        <button type="button" className="v2-cd-ctx-stepper-btn" onClick={() => onChangeWidth(clamp(w + 1))} aria-label="Thicker outline">+</button>
      </div>
      {w > 0 && (
        <button
          type="button"
          className="v2-cd-ctx-colour-trigger v2-cd-ctx-outline-swatch"
          onClick={onOpenColor}
          aria-label="Outline colour"
        >
          <span className="v2-cd-ctx-colour-swatch" style={{ background: color || '#29282D' }} />
          <ChevronDown />
        </button>
      )}
    </div>
  );
};

// Font trigger — opens the side panel's Font editor for the selected
// element. No dropdown; consistent with the colour trigger.
const FontTrigger = ({ value, onOpen }) => {
  const current = FONT_OPTIONS.find(f => f.id === value) || FONT_OPTIONS[0];
  return (
    <button
      type="button"
      className="v2-cd-ctx-trigger"
      onClick={onOpen}
      style={{ fontFamily: current.family }}
    >
      {current.label} <ChevronDown />
    </button>
  );
};

// Colour trigger — opens the side panel's colour editor. Renders as a
// swatch + hex chip, same visual language as the InlineColourTrigger
// used in the Background panel.
const ColourTrigger = ({ value, onOpen }) => (
  <button
    type="button"
    className="v2-cd-ctx-colour-trigger"
    onClick={onOpen}
  >
    <span className="v2-cd-ctx-colour-swatch" style={{ background: value || '#FFFFFF' }} />
    <span className="v2-cd-ctx-colour-hex">{(value || '').toUpperCase()}</span>
    <ChevronDown />
  </button>
);

// Size stepper — minus / value / plus, clamped per element type.
const SizeStepper = ({ value, onChange, min = 8, max = 200 }) => {
  const clamp = (n) => Math.max(min, Math.min(max, n));
  return (
    <div className="v2-cd-ctx-stepper">
      <button type="button" className="v2-cd-ctx-stepper-btn" onClick={() => onChange(clamp((value || 22) - 2))} aria-label="Decrease size">−</button>
      <span className="v2-cd-ctx-stepper-value">{value || 22}</span>
      <button type="button" className="v2-cd-ctx-stepper-btn" onClick={() => onChange(clamp((value || 22) + 2))} aria-label="Increase size">+</button>
    </div>
  );
};

// B / I / U toggle group.
const StyleToggles = ({ bold, italic, underline, onChange }) => (
  <div className="v2-cd-ctx-toggles" role="group" aria-label="Text styles">
    <button
      type="button"
      className={`v2-cd-ctx-toggle ${bold ? 'is-active' : ''}`}
      onClick={() => onChange({ bold: !bold })}
      aria-pressed={!!bold}
      aria-label="Bold"
    ><IconBold /></button>
    <button
      type="button"
      className={`v2-cd-ctx-toggle ${italic ? 'is-active' : ''}`}
      onClick={() => onChange({ italic: !italic })}
      aria-pressed={!!italic}
      aria-label="Italic"
    ><IconItalic /></button>
    <button
      type="button"
      className={`v2-cd-ctx-toggle ${underline ? 'is-active' : ''}`}
      onClick={() => onChange({ underline: !underline })}
      aria-pressed={!!underline}
      aria-label="Underline"
    ><IconUnderline /></button>
  </div>
);

// Alignment toggle group.
const AlignToggles = ({ align, onChange }) => (
  <div className="v2-cd-ctx-toggles" role="group" aria-label="Alignment">
    {['left', 'center', 'right'].map((a) => {
      const Icon = a === 'left' ? IconAlignLeft : a === 'center' ? IconAlignCenter : IconAlignRight;
      return (
        <button
          key={a}
          type="button"
          className={`v2-cd-ctx-toggle ${align === a ? 'is-active' : ''}`}
          onClick={() => onChange({ align: a })}
          aria-pressed={align === a}
          aria-label={`Align ${a}`}
        ><Icon /></button>
      );
    })}
  </div>
);

const Divider = () => <div className="v2-cd-ctx-divider" aria-hidden />;

// ─── Multi-select toolbar (2+ elements selected) ───
// Align icons: a reference edge/line plus two bars sitting against it.
const IcAlignLeft = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden><rect x="1.4" y="2" width="1.3" height="12" rx=".6"/><rect x="4.2" y="3.8" width="6.5" height="2.4" rx="1"/><rect x="4.2" y="9.8" width="9.6" height="2.4" rx="1"/></svg>);
const IcAlignCenterH = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden><rect x="7.35" y="2" width="1.3" height="12" rx=".6"/><rect x="4.75" y="3.8" width="6.5" height="2.4" rx="1"/><rect x="3.2" y="9.8" width="9.6" height="2.4" rx="1"/></svg>);
const IcAlignRight = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden><rect x="13.3" y="2" width="1.3" height="12" rx=".6"/><rect x="5.3" y="3.8" width="6.5" height="2.4" rx="1"/><rect x="2.2" y="9.8" width="9.6" height="2.4" rx="1"/></svg>);
const IcAlignTop = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden><rect x="2" y="1.4" width="12" height="1.3" rx=".6"/><rect x="3.8" y="4.2" width="2.4" height="6.5" rx="1"/><rect x="9.8" y="4.2" width="2.4" height="9.6" rx="1"/></svg>);
const IcAlignMiddle = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden><rect x="2" y="7.35" width="12" height="1.3" rx=".6"/><rect x="3.8" y="4.75" width="2.4" height="6.5" rx="1"/><rect x="9.8" y="3.2" width="2.4" height="9.6" rx="1"/></svg>);
const IcAlignBottom = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden><rect x="2" y="13.3" width="12" height="1.3" rx=".6"/><rect x="3.8" y="5.3" width="2.4" height="6.5" rx="1"/><rect x="9.8" y="2.2" width="2.4" height="9.6" rx="1"/></svg>);
const IcDistH = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden><rect x="1.4" y="2.5" width="1.3" height="11" rx=".6"/><rect x="7.35" y="2.5" width="1.3" height="11" rx=".6"/><rect x="13.3" y="2.5" width="1.3" height="11" rx=".6"/></svg>);
const IcDistV = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden><rect x="2.5" y="1.4" width="11" height="1.3" rx=".6"/><rect x="2.5" y="7.35" width="11" height="1.3" rx=".6"/><rect x="2.5" y="13.3" width="11" height="1.3" rx=".6"/></svg>);
const IcGroup = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden><rect x="2.4" y="2.4" width="11.2" height="11.2" rx="2.2" strokeDasharray="2.4 1.8"/><rect x="5" y="5" width="6" height="6" rx="1.4" fill="currentColor" stroke="none"/></svg>);
const IcUngroup = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden><rect x="1.8" y="5.4" width="6.6" height="6.6" rx="1.5"/><rect x="7.6" y="2.6" width="6.6" height="6.6" rx="1.5"/></svg>);
const IcDuplicate = () => (<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="5" y="5" width="9" height="9" rx="1.5"/><path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2"/></svg>);
const IcTrash = () => (<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 4.5h10M6.5 4.5V3a1 1 0 011-1h1a1 1 0 011 1v1.5M4.5 4.5l.5 8a1 1 0 001 1h4a1 1 0 001-1l.5-8"/></svg>);

const CtxIconBtn = ({ label, onClick, danger, children }) => (
  <button
    type="button"
    className={`v2-cd-ctx-iconbtn ${danger ? 'v2-cd-ctx-iconbtn--danger' : ''}`}
    onClick={onClick}
    aria-label={label}
    title={label}
  >
    {children}
  </button>
);

const MultiSelectToolbar = ({
  count, isGroup, onAlign, onDistribute, onGroup, onUngroup,
  onDuplicateSelection, onDeleteSelection,
}) => (
  <div className="v2-cd-ctx">
    <span className="v2-cd-ctx-label">{count} selected</span>
    <Divider />
    <div className="v2-cd-ctx-iconrow" role="group" aria-label="Align">
      <CtxIconBtn label="Align left" onClick={() => onAlign('left')}><IcAlignLeft /></CtxIconBtn>
      <CtxIconBtn label="Align horizontal centres" onClick={() => onAlign('hcenter')}><IcAlignCenterH /></CtxIconBtn>
      <CtxIconBtn label="Align right" onClick={() => onAlign('right')}><IcAlignRight /></CtxIconBtn>
      <CtxIconBtn label="Align top" onClick={() => onAlign('top')}><IcAlignTop /></CtxIconBtn>
      <CtxIconBtn label="Align vertical centres" onClick={() => onAlign('vcenter')}><IcAlignMiddle /></CtxIconBtn>
      <CtxIconBtn label="Align bottom" onClick={() => onAlign('bottom')}><IcAlignBottom /></CtxIconBtn>
    </div>
    {count >= 3 && (
      <>
        <Divider />
        <div className="v2-cd-ctx-iconrow" role="group" aria-label="Distribute">
          <CtxIconBtn label="Distribute horizontally" onClick={() => onDistribute('x')}><IcDistH /></CtxIconBtn>
          <CtxIconBtn label="Distribute vertically" onClick={() => onDistribute('y')}><IcDistV /></CtxIconBtn>
        </div>
      </>
    )}
    <Divider />
    {isGroup
      ? <CtxIconBtn label="Ungroup" onClick={onUngroup}><IcUngroup /></CtxIconBtn>
      : <CtxIconBtn label="Group" onClick={onGroup}><IcGroup /></CtxIconBtn>}
    <Divider />
    <CtxIconBtn label="Duplicate" onClick={onDuplicateSelection}><IcDuplicate /></CtxIconBtn>
    <CtxIconBtn label="Delete" danger onClick={onDeleteSelection}><IcTrash /></CtxIconBtn>
  </div>
);

const DesignerTopContextBar = ({
  element,
  multiCount = 0,
  isGroup,
  onAlign,
  onDistribute,
  onGroup,
  onUngroup,
  onDuplicateSelection,
  onDeleteSelection,
  onChange,
  onOpenFontPanel,
  onOpenColorPanel,
  onOpenStrokePanel,
}) => {
  // 2+ selected → align / distribute / group toolbar.
  if (multiCount >= 2) {
    return (
      <MultiSelectToolbar
        count={multiCount}
        isGroup={isGroup}
        onAlign={onAlign}
        onDistribute={onDistribute}
        onGroup={onGroup}
        onUngroup={onUngroup}
        onDuplicateSelection={onDuplicateSelection}
        onDeleteSelection={onDeleteSelection}
      />
    );
  }
  if (!element) return null;

  // ─── Text + Amount ───
  if (element.type === 'text' || element.type === 'amount') {
    return (
      <div className="v2-cd-ctx">
        <span className="v2-cd-ctx-label">{element.type === 'text' ? 'Text' : 'Amount'}</span>
        <Divider />
        <FontTrigger
          value={element.font || 'fraunces'}
          onOpen={onOpenFontPanel}
        />
        <Divider />
        <SizeStepper
          value={element.fontSize}
          onChange={(fontSize) => onChange({ fontSize })}
          min={8}
          max={200}
        />
        <Divider />
        <ColourTrigger
          value={element.color || '#FFFFFF'}
          onOpen={onOpenColorPanel}
        />
        {element.type === 'text' && (
          <>
            <Divider />
            <StyleToggles
              bold={element.bold}
              italic={element.italic}
              underline={element.underline}
              onChange={onChange}
            />
            <Divider />
            <AlignToggles align={element.align || 'center'} onChange={onChange} />
          </>
        )}
      </div>
    );
  }

  // ─── Shape ───
  if (element.type === 'shape') {
    // Circles are already round, and the heart's curves aren't a polygon,
    // so the corner control only applies to the rest.
    const canRound = element.shape !== 'circle' && element.shape !== 'heart';
    return (
      <div className="v2-cd-ctx">
        <span className="v2-cd-ctx-label">Shape</span>
        <Divider />
        <ColourTrigger
          value={element.fill || '#FFFFFF'}
          onOpen={onOpenColorPanel}
        />
        {canRound && (
          <>
            <Divider />
            <RoundnessControl value={element.radius} onChange={onChange} />
          </>
        )}
        <Divider />
        <OutlineControl
          width={element.strokeWidth || 0}
          color={element.stroke || '#29282D'}
          onChangeWidth={(w) =>
            onChange(w > 0
              ? { strokeWidth: w, stroke: element.stroke || '#29282D' }
              : { strokeWidth: 0 })
          }
          onOpenColor={onOpenStrokePanel}
        />
      </div>
    );
  }

  // ─── Image ───
  if (element.type === 'image') {
    return (
      <div className="v2-cd-ctx">
        <span className="v2-cd-ctx-label">Image</span>
      </div>
    );
  }

  return null;
};

export default DesignerTopContextBar;
